// Decode a file to its original channels and sample rate — no downmix, no
// resample. This is the format-conversion counterpart to decodeAudio.js,
// which deliberately does the opposite (forces mono 16 kHz for Whisper).
//
// Decoding tiers, cheapest first:
//   1. WAV files go through our own decoder (wavDecoder.js), which covers the
//      legacy codecs (µ-law, A-law, ADPCM) browsers refuse.
//   2. G.729-tagged WAVs (format 0x2222/0x0083 — GTL's "ICMV" branding is the
//      common one) go through a small native wasm decoder (g729Decode.js)
//      instead of falling all the way to ffmpeg.wasm — it's the overwhelming
//      majority of "unsupported codec" files this tool sees, and the wasm
//      module is under 50 KB, bundled with the app.
//   3. Everything else (and any WAV codec neither of the above implements)
//      goes to the browser's decodeAudioData, which covers MP3, M4A/AAC,
//      FLAC, OGG and the audio track of MP4/MOV.
//   4. ffmpeg.wasm (ffmpegDecode.js) — the true last resort, only ever
//      reached after every faster path fails. Covers GSM 6.10, AMR, and
//      containers like MKV/AVI/WMV. Lazy: nothing about it downloads unless
//      a file actually needs it.
//
// All tiers run entirely in the tab — no audio is uploaded at any point.

import {
  isRiffWave,
  decodeWav,
  extractEmbeddedStream,
  UnsupportedWavFormatError,
  G729_FORMAT_TAGS,
} from './wavDecoder';

function AudioCtx() {
  return window.AudioContext || window.webkitAudioContext;
}

async function decodeAtNativeRate(arrayBuffer) {
  const Ctx = AudioCtx();
  const ctx = new Ctx();
  try {
    const buf = await new Promise((resolve, reject) => {
      const maybePromise = ctx.decodeAudioData(arrayBuffer, resolve, reject);
      if (maybePromise?.then) maybePromise.then(resolve, reject);
    });
    return buf;
  } finally {
    ctx.close?.();
  }
}

function toChannelArray(audioBuffer) {
  const channels = [];
  for (let c = 0; c < audioBuffer.numberOfChannels; c++) {
    channels.push(audioBuffer.getChannelData(c).slice());
  }
  return channels;
}

/** The true last resort: ffmpeg.wasm, lazily imported so nothing about it
 *  ever touches the bundle or the network unless this line actually runs. */
async function decodeWithFfmpegFallback(file, onEvent) {
  const { decodeWithFfmpeg } = await import('./ffmpegDecode');
  return decodeWithFfmpeg(file, onEvent);
}

/** G.729's fast native path — lazily imported like the ffmpeg fallback. */
async function decodeWithG729Fallback(arrayBuffer) {
  const { decodeG729Wav } = await import('./g729Decode');
  const { channels, sampleRate, formatName } = await decodeG729Wav(arrayBuffer);
  return { channels, sampleRate, sourceFormat: formatName };
}

/**
 * @param {File} file
 * @param {(msg: object) => void} [onEvent] progress sink for the ffmpeg
 *   fallback only — every faster path is silent because it's fast.
 * @returns {Promise<{ channels: Float32Array[], sampleRate: number, sourceFormat: string|null }>}
 */
export async function decodeToOriginal(file, onEvent = () => {}) {
  const arrayBuffer = await file.arrayBuffer();

  if (isRiffWave(arrayBuffer)) {
    // Some WAVs just wrap an MP3 stream; let the browser handle those natively.
    const embedded = extractEmbeddedStream(arrayBuffer);
    if (embedded) {
      try {
        const buf = await decodeAtNativeRate(embedded.payload);
        return { channels: toChannelArray(buf), sampleRate: buf.sampleRate, sourceFormat: 'MP3 (in WAV container)' };
      } catch {
        // Fall through to the paths below.
      }
    }

    try {
      const { channels, sampleRate, formatName } = decodeWav(arrayBuffer);
      return { channels, sampleRate, sourceFormat: formatName };
    } catch (err) {
      // G.729 ("ICMV") is the overwhelming majority of codecs this tool sees
      // that the browser can't touch either — try the small native decoder
      // before paying for a browser round-trip that's certain to fail. If
      // this file uses a G.729 layout the native decoder doesn't yet handle
      // (e.g. more than one channel), fall through to the same chain that
      // ran before this tier existed — never turn a case ffmpeg.wasm could
      // still handle into a hard failure.
      if (err instanceof UnsupportedWavFormatError && G729_FORMAT_TAGS.has(err.formatTag)) {
        try {
          return await decodeWithG729Fallback(arrayBuffer);
        } catch {
          // Fall through below.
        }
      }

      // A codec we don't implement may still be one the browser knows.
      try {
        const buf = await decodeAtNativeRate(arrayBuffer.slice(0));
        return { channels: toChannelArray(buf), sampleRate: buf.sampleRate, sourceFormat: null };
      } catch {
        // Last resort before giving up: ffmpeg.wasm.
        try {
          return await decodeWithFfmpegFallback(file, onEvent);
        } catch (ffmpegErr) {
          if (err instanceof UnsupportedWavFormatError) {
            throw new Error(`${err.message} (Fallback decoder also failed: ${ffmpegErr.message})`);
          }
          throw err;
        }
      }
    }
  }

  try {
    const buf = await decodeAtNativeRate(arrayBuffer);
    return { channels: toChannelArray(buf), sampleRate: buf.sampleRate, sourceFormat: null };
  } catch {
    // Last resort before giving up: ffmpeg.wasm — this is also what catches
    // containers (MKV/AVI/WMV) the browser can't demux at all.
    try {
      return await decodeWithFfmpegFallback(file, onEvent);
    } catch {
      throw new Error(
        `Your browser could not decode "${file.name}", and the fallback decoder couldn't either. ` +
        `It handles MP3, WAV, M4A, FLAC, OGG, and the audio track of most MP4/MOV files, plus most ` +
        `other formats via a slower built-in fallback — but this file didn't match anything.`
      );
    }
  }
}
